import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {SubscribedComponent} from '../../subscribed.component';
import {OrganizationService} from '../../shared/organization.service';
import {Organization} from '../../interfaces/client';
import {PageMeta} from '../../interfaces/organization-vacancy';
import {Paged} from '../../interfaces/paged';

/** Tracking state derived from my_client, for the row chip. */
type Tracking = 'untracked' | 'prospect' | 'client' | 'archived';

/**
 * Browsable global Organisations directory. Rows carry the canonical name, a
 * tenant-scoped vacancy count, and this Company's own tracking status.
 *
 * Gated to admin/staffer with crm_enabled by the route guard and the sidebar,
 * matching the other two CRM pages. Note the per-org vacancy endpoint used by
 * the detail page is deliberately gated more broadly.
 */
@Component({
  selector: 'bst-organizations',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss'],
  standalone: false,
})
export class OrganizationsComponent extends SubscribedComponent implements OnInit {

  public organizations: Organization[] = [];
  public meta: PageMeta | null = null;
  public paged: Paged<Organization> | null = null;
  public errorMessage: string | null = null;
  public searchQuery = '';
  public sort: 'canonical_name' | 'created_at' = 'canonical_name';
  public direction: 'asc' | 'desc' = 'asc';
  public perPage = 50;
  /** Same options Market overview offers, so the two lists page alike. */
  public readonly pageSizeOptions = [10, 25, 50, 100];

  private searchDebounce = new Subject<string>();

  /**
   * Which load() call owns the rows.
   *
   * Search debounce, sorting, paging and now the page-size selector all trigger
   * a load, so two can be in flight at once and the slower one can land last.
   * Market overview carries the same counter in its own delegate
   * (market-vacancies-list.component.ts) for exactly this reason; the control
   * being reused here does not bring the guard with it.
   */
  private loadGeneration = 0;

  constructor(
    private orgService: OrganizationService,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subs.push(this.searchDebounce.pipe(debounceTime(300)).subscribe(() => void this.load(1)));
    void this.load(1);
  }

  public onSearch(value: string): void {
    this.searchQuery = value;
    this.searchDebounce.next(value);
  }

  public toggleSort(column: 'canonical_name' | 'created_at'): void {
    if (this.sort === column) {
      this.direction = this.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sort = column;
      this.direction = 'asc';
    }
    void this.load(1);
  }

  public changePageSize(size: number): void {
    if (size === this.perPage) { return; }
    this.perPage = size;
    // Back to page 1: page 7 of 50-per-page is past the end at 100-per-page,
    // and landing on an empty page reads as "no organizations".
    void this.load(1);
  }

  public async load(page: number): Promise<void> {
    const generation = ++this.loadGeneration;
    this.loading.next(true);
    this.errorMessage = null;
    let res;
    try {
      res = await this.orgService.list({
        q: this.searchQuery,
        page,
        per_page: this.perPage,
        sort: this.sort,
        direction: this.direction,
      });
    } catch (e) {
      console.error('organizations load failed', e);
    }
    // A newer load() started while this one was in flight, so it owns the rows.
    // Without this, a slow page-1-of-100 landing after a fast page-1-of-10
    // repaints the list with rows the control no longer describes.
    if (generation !== this.loadGeneration) { return; }
    this.loading.next(false);
    if (!res) {
      this.errorMessage = 'Failed to load the organizations directory.';
      return;
    }
    this.organizations = res.data ?? [];
    this.meta = res.meta ?? null;
    this.paged = this.meta ? this.toPaged(this.meta, this.organizations) : null;
  }

  /**
   * The page meta in the shape bst-pager reads.
   *
   * The directory endpoint returns total/per_page/current_page/last_page and no
   * from/to, and this change keeps the backend contract untouched, so the two
   * the pager displays are derived. Both are 0 on an empty result rather than
   * the 1..0 the arithmetic gives unguarded.
   *
   * Assigned in load() rather than exposed as a getter: a getter building a new
   * object would hand *ngIf a fresh reference every change-detection pass, and
   * ngIf compares by reference, so it would destroy and rebuild the pager on
   * every tick.
   */
  private toPaged(meta: PageMeta, rows: Organization[]): Paged<Organization> {
    const {current_page, per_page, total, last_page} = meta;

    return {
      current_page, per_page, total, last_page,
      from: total === 0 ? 0 : (current_page - 1) * per_page + 1,
      to: total === 0 ? 0 : Math.min(current_page * per_page, total),
      data: rows,
    };
  }

  /**
   * ClientStatus is an int-backed enum on the backend:
   * 0 archived, 1 active, 2 prospect, 3 prospect-on-hold, 4 prospect-lost.
   */
  public tracking(org: Organization): Tracking {
    const status = org.my_client?.status;
    if (status === undefined || status === null) { return 'untracked'; }
    if (status === 1) { return 'client'; }
    if (status === 0) { return 'archived'; }
    return 'prospect';
  }

  public open(org: Organization): void {
    void this.router.navigate(['/organizations', org.id]);
  }
}
